import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import axiosInstance from '../services/BaseService';
import CommunicationService from '../services/CommunicationService';
import FeedbackButtons from '../components/FeedbackButtons';
import CustomerSupportWidget from '../components/CustomerSupportWidget';

interface BookingDetails {
  _id: string;
  bookingCode: string;
  customerName: string;
  phoneNumber: string;
  totalAmount: number;
  status: string;
  createdAt: string;
  scheduleId: {
    programId: {
      title: string;
    };
    startDate: string;
    timing: string;
    address: string;
  };
  tickets: Array<{
    ticketOptionId: {
      ticketName: string;
      price: number;
    };
    quantity: number;
  }>;
}

const BookingDetailsPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tickets/booking-enquiry/${bookingId}`);
      setBooking(response.data);
    } catch (err) {
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleShareBooking = async (channel: 'email' | 'sms' | 'whatsapp') => {
    if (!booking) return;
    
    try {
      await CommunicationService.shareBooking(
        booking.bookingCode,
        booking.scheduleId.programId.title,
        new Date(booking.scheduleId.startDate).toLocaleDateString(),
        booking.scheduleId.timing,
        booking.scheduleId.address,
        booking.tickets.map(t => `${t.quantity}x ${t.ticketOptionId.ticketName}`).join(', '),
        booking.totalAmount,
        booking.customerName,
        channel
      );
    } catch (error) {
      console.error('Error sharing booking:', error);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Loading booking details...</p>
      </Container>
    );
  }

  if (error || !booking) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Booking Not Found</h4>
          <p>{error || 'The requested booking could not be found.'}</p>
        </Alert>
      </Container>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending Payment</Badge>;
      case 'verification-pending':
        return <Badge bg="info">Verification Pending</Badge>;
      case 'confirmed':
        return <Badge bg="success">Confirmed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Header style={{ backgroundColor: '#810081', color: 'white' }}>
              <h4 className="mb-0">
                <i className="bi bi-ticket-perforated me-2"></i>
                Booking Details
              </h4>
            </Card.Header>
            <Card.Body className="p-4">
              {/* Booking Info */}
              <Row className="mb-4">
                <Col md={6}>
                  <h5 style={{ color: '#810081' }}>Booking Information</h5>
                  <div className="mb-2">
                    <strong>Booking Code:</strong> {booking.bookingCode}
                  </div>
                  <div className="mb-2">
                    <strong>Status:</strong> {getStatusBadge(booking.status)}
                  </div>
                  <div className="mb-2">
                    <strong>Booking Date:</strong> {new Date(booking.createdAt).toLocaleDateString()}
                  </div>
                </Col>
                <Col md={6}>
                  <h5 style={{ color: '#810081' }}>Customer Information</h5>
                  <div className="mb-2">
                    <strong>Name:</strong> {booking.customerName}
                  </div>
                  <div className="mb-2">
                    <strong>Phone:</strong> {booking.phoneNumber}
                  </div>
                </Col>
              </Row>

              {/* Show Details */}
              <Row className="mb-4">
                <Col xs={12}>
                  <h5 style={{ color: '#810081' }}>Show Details</h5>
                  <Card className="border-0" style={{ backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
                    <Card.Body>
                      <h6>{booking.scheduleId.programId.title}</h6>
                      <div className="d-flex flex-wrap gap-3">
                        <span><i className="bi bi-calendar me-1"></i>{new Date(booking.scheduleId.startDate).toLocaleDateString()}</span>
                        <span><i className="bi bi-clock me-1"></i>{booking.scheduleId.timing}</span>
                        <span><i className="bi bi-geo-alt me-1"></i>{booking.scheduleId.address}</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Ticket Details */}
              <Row className="mb-4">
                <Col xs={12}>
                  <h5 style={{ color: '#810081' }}>Ticket Details</h5>
                  {booking.tickets.map((ticket, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <div>
                        <strong>{ticket.ticketOptionId.ticketName}</strong>
                        <br />
                        <small className="text-muted">Quantity: {ticket.quantity}</small>
                      </div>
                      <div className="text-end">
                        <div>₹{ticket.ticketOptionId.price} × {ticket.quantity}</div>
                        <div><strong>₹{ticket.ticketOptionId.price * ticket.quantity}</strong></div>
                      </div>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between align-items-center py-3 border-top">
                    <h6 className="mb-0">Total Amount</h6>
                    <h5 className="mb-0" style={{ color: '#810081' }}>₹{booking.totalAmount}</h5>
                  </div>
                </Col>
              </Row>

              {/* Action Buttons */}
              <Row>
                <Col xs={12}>
                  <h5 style={{ color: '#810081' }} className="mb-3">Actions</h5>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <Button
                      onClick={() => handleShareBooking('whatsapp')}
                      className="d-flex align-items-center"
                      style={{ backgroundColor: '#25D366', border: 'none' }}
                    >
                      <i className="bi bi-whatsapp me-2"></i>
                      Share via WhatsApp
                    </Button>
                    <Button
                      onClick={() => handleShareBooking('email')}
                      variant="outline-primary"
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-envelope me-2"></i>
                      Share via Email
                    </Button>
                    <Button
                      onClick={() => handleShareBooking('sms')}
                      variant="outline-success"
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-chat-dots me-2"></i>
                      Share via SMS
                    </Button>
                  </div>
                  
                  {/* Feedback Button for confirmed bookings */}
                  {booking.status === 'confirmed' && (
                    <FeedbackButtons
                      booking={{
                        bookingCode: booking.bookingCode,
                        customerName: booking.customerName
                      }}
                      event={{
                        title: booking.scheduleId.programId.title,
                        date: new Date(booking.scheduleId.startDate).toLocaleDateString()
                      }}
                      variant="outline-primary"
                    />
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Customer Support Widget */}
      {/* <CustomerSupportWidget position="bottom-right" /> */}
    </Container>
  );
};

export default BookingDetailsPage;